import { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormikProvider, useFormik } from 'formik'
import { Text, TextArea, SelectBox, Number, Email, File, DatePicker } from 'components/input_fields'
import { UpdateProfileSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import { Loader } from 'components/loader'
import moment from 'moment'
import { UserRound, MapPin, CircleUserRound } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { countryList, stateByCountry, cityByState } from '../../../../redux/slices/master'
import { updateProfile } from '../../../../redux/slices/users'
import { toggleUpdateModal, toggleBool } from '../../../../redux/slices/globalSlice'
import { addUpdateHandler } from 'utils'
import { useTranslation } from 'react-i18next'

export default function UpdateProfileForm() {

    const [loading, setLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const details = useSelector(state => state?.profile?.details)
    const country = useSelector(state => state?.country?.list)
    const stateList = useSelector(state => state?.state?.byCountry)
    const city = useSelector(state => state?.city?.byState)
    const stateLoading = useSelector(state => state?.state?.loading)
    const cityLoading = useSelector(state => state?.city?.loading)

    const country_option = country?.map(val => (
        { "value": val.id, "label": val.country }
    ))

    const state_option = stateList?.map(val => (
        { "value": val.id, "label": val.state }
    ))

    const city_option = city?.map(val => (
        { "value": val.id, "label": val.city }
    ))

    const gender_option = [
        { "value": 'Male', "label": 'Male' },
        { "value": 'Female', "label": 'Female' },
        { "value": 'Other', "label": 'Other' },
    ]

    const userInfo = details?.userInfo

    const initialValues = {
        id: details?.id,
        first_name: userInfo?.first_name || '',
        last_name: userInfo?.last_name || '',
        mobile_number: userInfo?.user_mobile ? String(userInfo.user_mobile) : '',
        email: userInfo?.user_email || '',
        country: userInfo?.country_id || '',
        state: userInfo?.state_id || '',
        city: userInfo?.city_id || '',
        pincode: userInfo?.pincode || '',
        address: userInfo?.address || '',
        gender: userInfo?.gender || '',
        dob: (userInfo?.dob && moment(userInfo.dob, 'YYYY-MM-DD').format('DD/MM/YYYY')) || '',
        profile: '',
    }

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: UpdateProfileSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(updateProfile({ 'values': values })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: res?.payload?.data?.message,
                wrong_toast: `${t('common.wrong')}`,
                navigate_route: '',
                spinner: setSpinner,
                toggle: () => {
                    dispatch(toggleUpdateModal());
                    dispatch(toggleBool());
                }
            }))
        },
    })

    const { values, errors, touched, handleBlur, handleChange, setFieldValue, setFieldTouched } = formik

    const load_state = (id) => {
        if (id) {
            dispatch(stateByCountry({ 'id': id, navigate }))
            setFieldValue('state', '')
            setFieldValue('city', '')
        } else {
            setFieldValue('state', '')
            setFieldValue('city', '')
        }
    }

    const load_city = (id) => {
        if (id) {
            dispatch(cityByState({ 'id': id, navigate }))
            setFieldValue('city', '')
        } else {
            setFieldValue('city', '')
        }
    }

    useEffect(() => {
        const loadOptions = async () => {
            setLoading(true)
            try {
                const country_id = userInfo?.country_id
                const state_id = userInfo?.state_id
                const requests = [dispatch(countryList({ navigate }))]
                if (country_id) {
                    requests.push(dispatch(stateByCountry({ 'id': country_id, navigate })))
                }
                if (state_id) {
                    requests.push(dispatch(cityByState({ 'id': state_id, navigate })))
                }
                await Promise.all(requests)
            } finally {
                setLoading(false)
            }
        }
        loadOptions()
    }, [])

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center w-100 py-4">
                <Loader />
            </div>
        )
    }

    return (
        <div className="container-fluid">
            <FormikProvider value={formik}>
                <Form onSubmit={(e) => { e.preventDefault(); formik.handleSubmit(); }}>
                    <div className="row">

                        <div className="col-md-12 mb-2">
                            <h6 className="form_section_title">
                                <UserRound size={18} />
                                {t('user.basic_info')}
                            </h6>
                        </div>

                        <div className="col-md-3">
                            <Text
                                label_name={t('user.fname')}
                                mandatory={true}
                                placeholder=''
                                disabled={false}
                                name='first_name'
                                value={values.first_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.first_name && touched.first_name ? (<span className='field_error'>{errors.first_name}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <Text
                                label_name={t('user.lname')}
                                mandatory={true}
                                placeholder=''
                                disabled={false}
                                name='last_name'
                                value={values.last_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.last_name && touched.last_name ? (<span className='field_error'>{errors.last_name}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <Number
                                label_name={t('user.mobile')}
                                mandatory={true}
                                placeholder=''
                                disabled={false}
                                name='mobile_number'
                                value={values.mobile_number}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.mobile_number && touched.mobile_number ? (<span className='field_error'>{errors.mobile_number}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <Email
                                label_name={t('user.email')}
                                mandatory={true}
                                placeholder=''
                                disabled={false}
                                name='email'
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.email && touched.email ? (<span className='field_error'>{errors.email}</span>) : null}
                            />
                        </div>

                        <div className="col-md-12 mt-3 mb-2">
                            <h6 className="form_section_title">
                                <MapPin size={18} />
                                {t('user.address_info')}
                            </h6>
                        </div>

                        <div className="col-md-3">
                            <SelectBox
                                label_name={t('user.country')}
                                mandatory={false}
                                placeholder=''
                                disabled={false}
                                option={country_option}
                                name='country'
                                value={values.country}
                                onChange={(e) => {
                                    load_state(e);
                                    setFieldValue('country', e);
                                }}
                                onBlur={() => setFieldTouched("country", true)}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                error={errors.country && touched.country ? (<span className='field_error'>{errors.country}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <SelectBox
                                label_name={t('user.state')}
                                mandatory={false}
                                placeholder=''
                                disabled={!values.country}
                                loading={stateLoading}
                                option={state_option}
                                name='state'
                                value={values.state}
                                onChange={(e) => {
                                    load_city(e);
                                    setFieldValue('state', e);
                                }}
                                onBlur={() => setFieldTouched("state", true)}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                error={errors.state && touched.state ? (<span className='field_error'>{errors.state}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <SelectBox
                                label_name={t('user.city')}
                                mandatory={false}
                                placeholder=''
                                disabled={!values.state}
                                loading={cityLoading}
                                option={city_option}
                                name='city'
                                value={values.city}
                                onChange={(e) =>
                                    setFieldValue('city', e)
                                }
                                onBlur={() => setFieldTouched("city", true)}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                error={errors.city && touched.city ? (<span className='field_error'>{errors.city}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <Number
                                label_name={t('user.pincode')}
                                mandatory={false}
                                placeholder=''
                                disabled={false}
                                name='pincode'
                                value={values.pincode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.pincode && touched.pincode ? (<span className='field_error'>{errors.pincode}</span>) : null}
                            />
                        </div>

                        <div className="col-md-6">
                            <TextArea
                                label_name={t('user.address')}
                                mandatory={false}
                                placeholder=''
                                disabled={false}
                                rows={2}
                                name='address'
                                value={values.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.address && touched.address ? (<span className='field_error'>{errors.address}</span>) : null}
                            />
                        </div>

                        <div className="col-md-12 mt-3 mb-2">
                            <h6 className="form_section_title">
                                <CircleUserRound size={18} />
                                {t('user.other_info')}
                            </h6>
                        </div>

                        <div className="col-md-3">
                            <SelectBox
                                label_name={t('user.gender')}
                                mandatory={false}
                                placeholder=''
                                disabled={false}
                                option={gender_option}
                                name='gender'
                                value={values.gender}
                                onChange={(e) =>
                                    setFieldValue('gender', e)
                                }
                                onBlur={() => setFieldTouched("gender", true)}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                error={errors.gender && touched.gender ? (<span className='field_error'>{errors.gender}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <DatePicker
                                label_name={t('user.dob')}
                                mandatory={false}
                                placeholder=''
                                name='dob'
                                value={values.dob}
                                onChange={(date, dateString) => setFieldValue('dob', dateString)}
                                onBlur={() => setFieldTouched('dob', true)}
                                error={errors.dob && touched.dob ? (<span className='field_error'>{errors.dob}</span>) : null}
                            />
                        </div>

                        <div className="col-md-3">
                            <File
                                label_name={t('user.profile')}
                                mandatory={false}
                                disabled={false}
                                name='profile'
                                onChange={(e) =>
                                    setFieldValue('profile', e.currentTarget.files[0])
                                }
                                onBlur={handleBlur}
                                error={errors.profile && touched.profile ? (<span className='field_error'>{errors.profile}</span>) : null}
                                accept="image/jpg, image/jpeg, image/png"
                            />
                        </div>

                        <div className="col-md-12">
                            <div className="d-flex justify-content-end">
                                <div className="col-lg-3">
                                    <SaveButton
                                        button_name={t('common.submit')}
                                        spinner={spinner}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                </Form>
            </FormikProvider>
        </div>
    )
}